/* ==========================================================================
   BULK UPSELL  —  Fantastic Decor Floral
   Replaces the BOGOS integration.

   How it works
   1. Each "Bulk upsell offer" block renders a hidden panel for one trigger
      variant. This script shows the panel whose trigger matches the selected
      variant and hides the rest.
   2. When the shopper clicks Add to cart with an add-on ticked, the add-on is
      added to the cart FIRST (/cart/add.js), then the theme's normal Add to
      cart runs. The theme therefore opens its cart drawer with both items
      already in the cart — no drawer refresh hacks needed.
   3. If nothing is ticked and the block has the last-chance popup enabled,
      a small popup offers the add-on once before the theme adds the item.
   4. The discount itself is applied by Shopify's automatic Buy X Get Y
      discount. This script never sets prices.
   ========================================================================== */

(function () {
  if (window.__bulkUpsellLoaded) return;
  window.__bulkUpsellLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ADD_BUTTON = '[data-js-product-add-to-cart]';

  function initProductPage(productPage) {
    if (productPage.dataset.bulkUpsellReady === 'true') return;

    var offers = Array.prototype.slice.call(productPage.querySelectorAll('[data-bulk-upsell]'));
    var picker = productPage.querySelector('product-variants');
    var productForm = productPage.querySelector('product-form');
    var form = productForm && productForm.querySelector('form');

    if (!offers.length || !form) return;
    productPage.dataset.bulkUpsellReady = 'true';

    var bypassNextSubmit = false;
    var busy = false;
    var declined = {};

    /* ---------- helpers ---------- */

    function currentVariantId() {
      if (picker && picker.currentVariant) return String(picker.currentVariant.id);
      var input = form.querySelector('input[name="id"]');
      return input ? String(input.value) : '';
    }

    function activeOffer() {
      for (var i = 0; i < offers.length; i++) {
        if (!offers[i].hidden) return offers[i];
      }
      return null;
    }

    function addonInputs(offer) {
      return Array.prototype.slice.call(offer.querySelectorAll('[data-bulk-upsell-addon]'));
    }

    function selectedAddon(offer) {
      var input = offer.querySelector('[data-bulk-upsell-addon]:checked');
      return input ? input.value : null;
    }

    function updateTotal(offer) {
      var box = offer.querySelector('[data-bulk-upsell-total]');
      var price = offer.querySelector('[data-bulk-upsell-total-price]');
      if (!box || !price) return;
      var input = offer.querySelector('[data-bulk-upsell-addon]:checked');
      if (input) {
        price.textContent = input.dataset.bundleTotal || '';
        box.hidden = false;
      } else {
        price.textContent = '';
        box.hidden = true;
      }
    }

    function clearSelection(offer) {
      addonInputs(offer).forEach(function (input) { input.checked = false; });
      updateTotal(offer);
    }

    function setBusy(button, state) {
      if (!button) return;
      if (state) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    /* ---------- show the right offer ---------- */

    function sync() {
      var id = currentVariantId();
      offers.forEach(function (offer) {
        offer.hidden = offer.dataset.triggerVariant !== id;
        clearSelection(offer);
      });
    }

    /* ---------- tick / untick (one add-on at most) ---------- */

    offers.forEach(function (offer) {
      offer.addEventListener('change', function (event) {
        var input = event.target.closest('[data-bulk-upsell-addon]');
        if (!input) return;
        if (input.checked) {
          addonInputs(offer).forEach(function (other) {
            if (other !== input) other.checked = false;
          });
        }
        updateTotal(offer);
      });

      var dialog = offer.querySelector('[data-bulk-upsell-dialog]');
      if (dialog) {
        // Click on the backdrop closes the popup (counts as "no thanks").
        dialog.addEventListener('click', function (event) {
          if (event.target === dialog) dialog.close('');
        });
      }
    });

    /* ---------- last-chance popup ---------- */

    function askLastChance(offer) {
      var dialog = offer.querySelector('[data-bulk-upsell-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return Promise.resolve(null);
      return new Promise(function (resolve) {
        dialog.returnValue = '';
        dialog.addEventListener('close', function () {
          resolve(dialog.returnValue || null);
        }, { once: true });
        dialog.showModal();
      });
    }

    /* ---------- cart ---------- */

    function addAddonToCart(variantId, offer) {
      var offerName = offer.dataset.offerName || 'Bulk offer';
      var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

      // Skip if this offer's add-on is already in the cart (prevents duplicates
      // from repeat clicks; Shopify only discounts one per order anyway).
      return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : { items: [] }; })
        .catch(function () { return { items: [] }; })
        .then(function (cart) {
          var already = (cart.items || []).some(function (item) {
            return String(item.variant_id) === String(variantId) &&
              item.properties && item.properties._bulk_offer === offerName;
          });
          if (already) return null;

          return fetch(ROOT + 'cart/add.js', {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
              items: [{
                id: Number(variantId),
                quantity: 1,
                properties: { _bulk_offer: offerName }
              }]
            })
          }).then(function (res) {
            if (!res.ok) {
              return res.json().catch(function () { return {}; }).then(function (data) {
                throw new Error(data.description || data.message || ('HTTP ' + res.status));
              });
            }
            document.dispatchEvent(new CustomEvent('bulk-upsell:added', {
              detail: { variantId: Number(variantId), offer: offerName }
            }));
            return res.json();
          });
        });
    }

    function resubmit(submitter) {
      bypassNextSubmit = true;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
      } else if (submitter) {
        submitter.click();
      } else {
        bypassNextSubmit = false;
      }
    }

    /* ---------- intercept Add to cart ----------
       Capture-phase listener on <product-form>, so it runs before the theme's
       own submit handler on the <form>. */

    productForm.addEventListener('submit', function (event) {
      if (event.target !== form) return;

      if (bypassNextSubmit) {
        bypassNextSubmit = false;
        return; // let the theme add the main product normally
      }

      // Only the normal Add to cart button (not Buy it now / other submits).
      var submitter = event.submitter || null;
      if (submitter && !submitter.matches(ADD_BUTTON)) return;
      if (!submitter) submitter = form.querySelector(ADD_BUTTON);

      var offer = activeOffer();
      if (!offer) return;

      var addonId = selectedAddon(offer);
      var trigger = offer.dataset.triggerVariant;
      var canAsk = !addonId &&
        offer.querySelector('[data-bulk-upsell-dialog]') &&
        !declined[trigger];

      if (!addonId && !canAsk) return; // nothing for us to do

      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      busy = true;

      var chosen = addonId ? Promise.resolve(addonId) : askLastChance(offer);

      chosen
        .then(function (id) {
          if (!id) {
            declined[trigger] = true; // don't ask again on this page view
            return null;
          }
          setBusy(submitter, true);
          return addAddonToCart(id, offer);
        })
        .catch(function (error) {
          console.error('Bulk upsell: add-on could not be added.', error);
        })
        .then(function () {
          clearSelection(offer);
          setBusy(submitter, false);
          busy = false;
          resubmit(submitter);
        });
    }, true);

    /* ---------- variant changes ---------- */

    if (picker) {
      picker.addEventListener('VARIANT_CHANGE', function () {
        window.setTimeout(sync, 50);
      });
    }

    sync();
  }

  function initAll(root) {
    (root || document).querySelectorAll('product-page').forEach(initProductPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when the product section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();